import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Modal,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Link,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  SelectChangeEvent,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import LaunchIcon from "@mui/icons-material/Launch";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  collection,
  addDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  doc,
  updateDoc,
} from "firebase/firestore";
import { firebaseHelper } from "ui";
import { DesignFirmType, DesignFirmStatus } from "ts-interfaces";
import axios from "axios";
import { useStaging } from "../libs/firebase";
import { useImpersonateDesignFirm } from "../ImpersonateDesignFirm";
import { getSearchApiBaseUrl } from "ui";
import { getAuth } from "firebase/auth";
import { CustomSnackbar } from "./SnackbarComponent";

const DesignFirmsManager = () => {
  const subscriptionURL = useStaging
    ? "https://us-central1-mattoboard-staging.cloudfunctions.net/StripeProcessor5/getSubscriptionByID"
    : "https://us-east1-mattoboard-b8284.cloudfunctions.net/StripeProcessor5/getSubscriptionByID";
  const customerURL = useStaging
    ? "https://us-central1-mattoboard-staging.cloudfunctions.net/StripeProcessor5/getCustomerByID"
    : "https://us-east1-mattoboard-b8284.cloudfunctions.net/StripeProcessor5/getCustomerByID";

  // Initialize all state variables
  const [designFirms, setDesignFirms] = useState<DesignFirmType[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentFirmId, setCurrentFirmId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [grantDialogOpen, setGrantDialogOpen] = useState(false);
  const [grantFirm, setGrantFirm] = useState<DesignFirmType | null>(null);
  const [grantAmount, setGrantAmount] = useState("");
  const [grantError, setGrantError] = useState("");
  const [granting, setGranting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [creditBalances, setCreditBalances] = useState<
    Record<string, { balance: number | null; loading: boolean; error?: string }>
  >({});
  const [newFirmData, setNewFirmData] = useState<Partial<DesignFirmType>>({
    name: "",
    domain: "",
    seats: 1,
    stripeCustomerId: "",
    stripeSubscriptionId: "",
    status: DesignFirmStatus.ACTIVE,
    website: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const requestedBalancesRef = useRef(new Set<string>());

  // Fetch design firms data with ordering
  useEffect(() => {
    try {
      const q = query(
        collection(firebaseHelper.db, "design_firms"),
        orderBy("created", "desc"),
      );
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const firmsData: DesignFirmType[] = [];
        querySnapshot.forEach((doc) => {
          firmsData.push({ id: doc.id, ...doc.data() } as DesignFirmType);
        });
        setDesignFirms(firmsData);
      });

      // Clean up the listener when component unmounts
      return () => unsubscribe();
    } catch (error) {
      console.error("Error fetching design firms:", error);
    }
  }, []);

  // Function to normalize domain (remove subdomains, lowercase)
  const normalizeDomain = (domain: string): string => {
    if (!domain) return "";

    try {
      // Remove protocol if present
      let normalizedDomain = domain.toLowerCase().trim();
      normalizedDomain = normalizedDomain.replace(/^(https?:\/\/)/i, "");

      // Remove trailing slashes and paths
      normalizedDomain = normalizedDomain.split("/")[0];

      // Remove any trailing periods
      normalizedDomain = normalizedDomain.replace(/\.$/, "");

      // Ensure we're only using the host portion (without any query parameters)
      normalizedDomain = normalizedDomain.split("?")[0];

      return normalizedDomain;
    } catch (error) {
      console.error("Error normalizing domain:", error);
      return domain.toLowerCase().trim();
    }
  };

  // Function to format date from Firestore timestamp
  const formatDate = (timestamp: any): string => {
    if (!timestamp) return "--";
    try {
      return new Date(timestamp.seconds * 1000).toLocaleDateString();
    } catch (error) {
      return "--";
    }
  };

  // Modal handlers
  const handleOpenModal = (firm: DesignFirmType | null = null) => {
    if (firm) {
      // Edit mode
      setIsEditMode(true);
      setCurrentFirmId(firm.id);
      setNewFirmData({
        name: firm.name || "",
        domain: firm.domain || "",
        seats: firm.seats || 1,
        stripeCustomerId: firm.stripeCustomerId || "",
        stripeSubscriptionId: firm.stripeSubscriptionId || "",
        status: firm.status || DesignFirmStatus.ACTIVE,
        website: firm.website || "",
      });
    } else {
      // Create mode
      setIsEditMode(false);
      setCurrentFirmId(null);
      setNewFirmData({
        name: "",
        domain: "",
        seats: 1,
        stripeCustomerId: "",
        stripeSubscriptionId: "",
        status: DesignFirmStatus.ACTIVE,
        website: "",
      });
    }
    setErrorMessage("");
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setIsEditMode(false);
    setCurrentFirmId(null);
    setErrorMessage("");
    // Reset form data
    setNewFirmData({
      name: "",
      domain: "",
      seats: 1,
      stripeCustomerId: "",
      stripeSubscriptionId: "",
      status: DesignFirmStatus.ACTIVE,
      website: "",
    });
  };

  const handleOpenGrantDialog = (firm: DesignFirmType) => {
    if (!firm?.stripeCustomerId) {
      return;
    }
    setGrantFirm(firm);
    setGrantAmount("");
    setGrantError("");
    setGrantDialogOpen(true);
  };

  const handleCloseGrantDialog = () => {
    setGrantDialogOpen(false);
    setGrantFirm(null);
    setGrantAmount("");
    setGrantError("");
  };

  const fetchCreditBalance = async (firmId: string, force = false) => {
    if (!firmId) return;
    if (!force && requestedBalancesRef.current.has(firmId)) return;
    requestedBalancesRef.current.add(firmId);

    setCreditBalances((prev) => ({
      ...prev,
      [firmId]: {
        balance: prev[firmId]?.balance ?? null,
        loading: true,
        error: undefined,
      },
    }));

    try {
      const auth = getAuth();
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) {
        throw new Error("Missing auth token.");
      }

      const response = await fetch(
        `${getSearchApiBaseUrl()}/api/credits/team/${firmId}/wallet`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to load balance.");
      }

      const data = await response.json();
      setCreditBalances((prev) => ({
        ...prev,
        [firmId]: {
          balance: data.balance?.stripeCredits ?? 0,
          loading: false,
          error: undefined,
        },
      }));
    } catch (error: any) {
      setCreditBalances((prev) => ({
        ...prev,
        [firmId]: {
          balance: prev[firmId]?.balance ?? null,
          loading: false,
          error: error?.message || "Failed to load balance.",
        },
      }));
    }
  };

  const handleGrantCredits = async () => {
    if (!grantFirm?.id) return;
    const amountValue = parseInt(grantAmount, 10);
    if (!amountValue || amountValue <= 0) {
      setGrantError("Please enter a valid credit amount.");
      return;
    }
    if (!grantFirm.stripeCustomerId) {
      setGrantError("Add a Stripe customer ID before granting credits.");
      return;
    }

    setGranting(true);
    setGrantError("");
    try {
      const auth = getAuth();
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) {
        throw new Error("Missing auth token.");
      }

      const response = await fetch(
        `${getSearchApiBaseUrl()}/api/credits/team/${grantFirm.id}/grant`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            amount: amountValue,
            reason: "Manual grant from design-firm superuser",
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to grant credits.");
      }

      setSnackbarMessage(
        `Granted ${amountValue} credits to ${grantFirm.name}.`,
      );
      setSnackbarOpen(true);
      await fetchCreditBalance(grantFirm.id, true);
      handleCloseGrantDialog();
    } catch (error: any) {
      setGrantError(error?.message || "Failed to grant credits.");
    } finally {
      setGranting(false);
    }
  };

  const handleInputChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent,
  ) => {
    const { name, value } = e.target as { name: string; value: unknown };
    setNewFirmData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Function to check if Stripe subscription exists
  const checkStripeSubscription = async (
    subscriptionId: string,
  ): Promise<boolean> => {
    try {
      const response = await axios.post(subscriptionURL, { subscriptionId });

      return response.status === 200;
    } catch (error) {
      console.error("Error validating Stripe subscription:", error);
      return false;
    }
  };

  const checkStripeCustomerId = async (
    customerId: string,
  ): Promise<boolean> => {
    try {
      const response = await axios.post(customerURL, { customerId });

      return response.status === 200;
    } catch (error) {
      console.error("Error validating Stripe customer:", error);
      return false;
    }
  };

  const handleCreateOrUpdateFirm = async () => {
    try {
      setErrorMessage("");
      setIsSubmitting(true);

      // Validate for duplicates
      const isDuplicate = designFirms.some((firm) => {
        // Skip current firm when checking for duplicates in edit mode
        if (isEditMode && firm.id === currentFirmId) return false;

        // Normalize domain for comparison (lowercase, no subdomains)
        const normalizedNewDomain = normalizeDomain(newFirmData.domain || "");
        const normalizedExistingDomain = normalizeDomain(firm.domain || "");

        // Check for duplicate name or domain
        return (
          firm.name.toLowerCase() === (newFirmData.name || "").toLowerCase() ||
          normalizedExistingDomain === normalizedNewDomain
        );
      });

      if (isDuplicate) {
        setErrorMessage(
          "A design firm with this name or domain already exists. Please use unique values.",
        );
        setIsSubmitting(false);
        return;
      }

      // Check if stripeSubscriptionId is unique if provided
      if (newFirmData.stripeSubscriptionId) {
        const isDuplicateSubscription = designFirms.some((firm) => {
          // Skip current firm when checking for duplicates in edit mode
          if (isEditMode && firm.id === currentFirmId) return false;

          return firm.stripeSubscriptionId === newFirmData.stripeSubscriptionId;
        });

        if (isDuplicateSubscription) {
          setErrorMessage(
            "The Stripe Subscription ID is already in use by another design firm.",
          );
          setIsSubmitting(false);
          return;
        }

        // Validate that the subscription ID exists in Stripe
        const subscriptionExists = await checkStripeSubscription(
          newFirmData.stripeSubscriptionId,
        );
        if (!subscriptionExists) {
          setErrorMessage(
            "The provided Stripe Subscription ID doesn't exist or is invalid. Please verify and try again.",
          );
          setIsSubmitting(false);
          return;
        }
      }

      // Check if stripeCustomerId is unique if provided
      if (newFirmData.stripeCustomerId) {
        const isDuplicateCustomer = designFirms.some((firm) => {
          // Skip current firm when checking for duplicates in edit mode
          if (isEditMode && firm.id === currentFirmId) return false;

          return firm.stripeCustomerId === newFirmData.stripeCustomerId;
        });

        if (isDuplicateCustomer) {
          setErrorMessage(
            "The Stripe Customer ID is already in use by another design firm.",
          );
          setIsSubmitting(false);
          return;
        }

        const customerExists = await checkStripeCustomerId(
          newFirmData.stripeCustomerId,
        );
        if (!customerExists) {
          setErrorMessage(
            "The provided Stripe Customer ID doesn't exist or is invalid. Please verify and try again.",
          );
          setIsSubmitting(false);
          return;
        }
      }

      // Normalize domain before saving
      const normalizedDomain = normalizeDomain(newFirmData.domain || "");

      // Ensure seats is converted to a number
      const seatsValue = parseInt(newFirmData.seats as unknown as string, 10);

      // Check if seats is a valid number and greater than 0
      if (isNaN(seatsValue) || seatsValue <= 0) {
        setErrorMessage("Seats must be a positive number.");
        setIsSubmitting(false);
        return;
      }

      // Create data object with seats converted to number
      const firmData = {
        ...newFirmData,
        seats: seatsValue,
        domain: normalizedDomain,
      };

      if (isEditMode && currentFirmId) {
        // Update existing firm
        const firmRef = doc(firebaseHelper.db, "design_firms", currentFirmId);
        await updateDoc(firmRef, {
          ...firmData,
          updated: serverTimestamp(),
          // Don't update created timestamp when editing
        });
      } else {
        // Create new firm
        const newFirm = {
          ...firmData,
          created: serverTimestamp(),
          updated: serverTimestamp(),
        };

        await addDoc(collection(firebaseHelper.db, "design_firms"), newFirm);
      }
      setIsSubmitting(false);
      handleCloseModal();
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} design firm: `,
        error,
      );
      setErrorMessage(
        `Error ${isEditMode ? "updating" : "creating"} design firm. Please try again.`,
      );
      setIsSubmitting(false);
    }
  };

  // Filter firms based on showArchived state
  const filteredDesignFirms = useMemo(
    () =>
      designFirms.filter(
        (firm) => showArchived || firm.status !== DesignFirmStatus.ARCHIVED,
      ),
    [designFirms, showArchived],
  );

  useEffect(() => {
    filteredDesignFirms.forEach((firm) => {
      if (firm?.id && firm.stripeCustomerId) {
        void fetchCreditBalance(firm.id);
      }
    });
  }, [filteredDesignFirms]);

  // Toggle archived visibility
  const toggleArchivedVisibility = () => {
    setShowArchived((prev) => !prev);
  };

  // Generate Stripe subscription URL
  const getStripeSubscriptionUrl = (subscriptionId: string): string => {
    if (!subscriptionId) return "";
    return useStaging
      ? `https://dashboard.stripe.com/test/subscriptions/${subscriptionId}`
      : `https://dashboard.stripe.com/subscriptions/${subscriptionId}`;
  };

  const getStripeCustomerUrl = (customerId: string): string => {
    if (!customerId) return "";
    return useStaging
      ? `https://dashboard.stripe.com/test/customers/${customerId}`
      : `https://dashboard.stripe.com/customers/${customerId}`;
  };

  const handleAssumeRole = (firm: DesignFirmType) => {
    useImpersonateDesignFirm.getState().setImpersonateDesignFirm(firm);
  };

  return (
    <Box
      sx={{
        width: "95%",
        paddingTop: 6,
        paddingLeft: 3,
        bgcolor: "background.default",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            color="text.primary"
          >
            Design Firms
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Manage and view all registered design firms
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={toggleArchivedVisibility}
            startIcon={
              showArchived ? <VisibilityOffIcon /> : <VisibilityIcon />
            }
            sx={{ borderRadius: 2 }}
          >
            {showArchived ? "Hide Archived" : "Show Archived"}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal()}
            sx={{ borderRadius: 2 }}
          >
            Create Design Firm
          </Button>
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          bgcolor: "background.paper",
          borderRadius: 1,
          overflow: "hidden",
          "& .MuiTableCell-root": { fontSize: "0.95rem", lineHeight: 1.4 },
          "& .MuiTableCell-head": { fontWeight: 600 },
        }}
      >
        <Table sx={{ tableLayout: "fixed" }}>
          <TableHead>
            <TableRow sx={{ borderBottom: "1px solid rgba(81, 81, 81, 1)" }}>
              <TableCell
                sx={{
                  color: "text.primary",
                  width: "14%",
                  padding: "16px",
                  borderBottom: "none",
                }}
              >
                Name
              </TableCell>
              <TableCell
                sx={{
                  color: "text.primary",
                  width: "14%",
                  padding: "16px",
                  borderBottom: "none",
                }}
              >
                Domain
              </TableCell>
              <TableCell
                sx={{
                  color: "text.primary",
                  width: "7%",
                  padding: "16px",
                  borderBottom: "none",
                }}
              >
                Seats
              </TableCell>
              <TableCell
                sx={{
                  color: "text.primary",
                  width: "14%",
                  padding: "16px",
                  borderBottom: "none",
                }}
              >
                Subscription
              </TableCell>
              <TableCell
                sx={{
                  color: "text.primary",
                  width: "14%",
                  padding: "16px",
                  borderBottom: "none",
                }}
              >
                Customer
              </TableCell>
              <TableCell
                sx={{
                  color: "text.primary",
                  width: "8%",
                  padding: "16px",
                  borderBottom: "none",
                }}
              >
                Created
              </TableCell>
              <TableCell
                sx={{
                  color: "text.primary",
                  width: "7%",
                  padding: "16px",
                  borderBottom: "none",
                }}
              >
                Status
              </TableCell>
              <TableCell
                sx={{
                  color: "text.primary",
                  width: "7%",
                  padding: "16px",
                  borderBottom: "none",
                }}
              >
                Credits
              </TableCell>
              <TableCell
                sx={{
                  color: "text.primary",
                  width: "5%",
                  padding: "16px",
                  borderBottom: "none",
                }}
              >
                Edit
              </TableCell>
              <TableCell
                sx={{
                  color: "text.primary",
                  width: "10%",
                  padding: "16px",
                  borderBottom: "none",
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDesignFirms.map((firm) => (
              <TableRow
                key={firm.id}
                sx={{
                  borderBottom: "1px solid rgba(81, 81, 81, 0.5)",
                  "&:hover": { bgcolor: "action.hover" },
                  bgcolor:
                    firm.status === DesignFirmStatus.ARCHIVED
                      ? "rgba(255, 152, 0, 0.08)"
                      : "inherit",
                }}
              >
                <TableCell
                  component="th"
                  scope="row"
                  sx={{
                    color: "text.primary",
                    width: "16%",
                    padding: "16px",
                    borderBottom: "none",
                    verticalAlign: "top",
                  }}
                >
                  {firm.name}
                </TableCell>
                <TableCell
                  sx={{
                    color: "text.primary",
                    width: "14%",
                    padding: "16px",
                    borderBottom: "none",
                    verticalAlign: "top",
                  }}
                >
                  {firm.domain || "--"}
                </TableCell>
                <TableCell
                  sx={{
                    color: "text.primary",
                    width: "7%",
                    padding: "16px",
                    borderBottom: "none",
                    verticalAlign: "top",
                  }}
                >
                  <Chip
                    label={firm.seats || 0}
                    size="small"
                    sx={{
                      bgcolor: "primary.main",
                      color: "white",
                      fontWeight: "bold",
                      borderRadius: "16px",
                      height: "24px",
                      fontSize: "0.75rem",
                    }}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    color: "text.primary",
                    width: "14%",
                    padding: "16px",
                    borderBottom: "none",
                    verticalAlign: "top",
                  }}
                >
                  {firm.stripeSubscriptionId ? (
                    <Link
                      href={getStripeSubscriptionUrl(firm.stripeSubscriptionId)}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        color: "primary.main",
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {firm.stripeSubscriptionId.substring(0, 14)}...
                      <LaunchIcon sx={{ ml: 0.5, fontSize: "0.8rem" }} />
                    </Link>
                  ) : (
                    "--"
                  )}
                </TableCell>
                <TableCell
                  sx={{
                    color: "text.primary",
                    width: "14%",
                    padding: "16px",
                    borderBottom: "none",
                    verticalAlign: "top",
                  }}
                >
                  {firm.stripeCustomerId ? (
                    <Link
                      href={getStripeCustomerUrl(firm.stripeCustomerId)}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        color: "primary.main",
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {firm.stripeCustomerId.substring(0, 14)}...
                      <LaunchIcon sx={{ ml: 0.5, fontSize: "0.8rem" }} />
                    </Link>
                  ) : (
                    "--"
                  )}
                </TableCell>
                <TableCell
                  sx={{
                    color: "text.primary",
                    width: "8%",
                    padding: "16px",
                    borderBottom: "none",
                    verticalAlign: "top",
                  }}
                >
                  {formatDate(firm.created)}
                </TableCell>
                <TableCell
                  sx={{
                    width: "7%",
                    padding: "16px",
                    borderBottom: "none",
                    verticalAlign: "top",
                  }}
                >
                  <Chip
                    variant="outlined"
                    label={firm.status || DesignFirmStatus.ACTIVE}
                    color={
                      firm.status === DesignFirmStatus.ACTIVE
                        ? "success"
                        : firm.status === DesignFirmStatus.ARCHIVED
                          ? "warning"
                          : "error"
                    }
                    size="small"
                    sx={{
                      bgcolor:
                        firm.status === DesignFirmStatus.ACTIVE
                          ? "#4caf50"
                          : firm.status === DesignFirmStatus.ARCHIVED
                            ? "#ff9800"
                            : "#f44336",
                      color: "#fff",
                      textTransform: "lowercase",
                      borderRadius: "16px",
                      height: "26px",
                      fontSize: "0.85rem",
                    }}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    width: "7%",
                    padding: "16px",
                    borderBottom: "none",
                    verticalAlign: "top",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {firm.stripeCustomerId ? (
                      creditBalances[firm.id]?.loading ? (
                        <CircularProgress size={16} />
                      ) : creditBalances[firm.id]?.error ? (
                        <Tooltip
                          title={
                            creditBalances[firm.id]?.error ||
                            "Failed to load balance."
                          }
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: "0.9rem" }}
                          >
                            --
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.primary"
                          sx={{ fontSize: "0.95rem" }}
                        >
                          {creditBalances[firm.id]?.balance ?? 0}
                        </Typography>
                      )
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.9rem" }}
                      >
                        --
                      </Typography>
                    )}
                    <Tooltip title="Refresh balance">
                      <span>
                        <IconButton
                          onClick={() =>
                            firm?.id && fetchCreditBalance(firm.id, true)
                          }
                          size="small"
                          color="primary"
                          disabled={
                            !firm.stripeCustomerId ||
                            creditBalances[firm.id]?.loading
                          }
                          sx={{ p: 0.5 }}
                        >
                          <RefreshIcon fontSize="inherit" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    width: "5%",
                    padding: "16px",
                    borderBottom: "none",
                    verticalAlign: "top",
                  }}
                >
                  <IconButton
                    onClick={() => handleOpenModal(firm)}
                    color="primary"
                    size="small"
                    sx={{ padding: "4px" }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
                <TableCell
                  sx={{
                    width: "8%",
                    padding: "12px",
                    borderBottom: "none",
                    verticalAlign: "top",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Button
                      onClick={() => {
                        handleAssumeRole(firm);
                      }}
                      color="primary"
                      size="small"
                      variant="outlined"
                      sx={{
                        borderRadius: "8px",
                        height: "30px",
                        textTransform: "none",
                        fontSize: "0.85rem",
                        px: 1.25,
                        minWidth: 0,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Assume Role
                    </Button>
                    <Tooltip
                      title={
                        firm.stripeCustomerId
                          ? ""
                          : "Add a Stripe customer ID to grant credits."
                      }
                      placement="top"
                      disableHoverListener={Boolean(firm.stripeCustomerId)}
                      disableFocusListener={Boolean(firm.stripeCustomerId)}
                      disableTouchListener={Boolean(firm.stripeCustomerId)}
                    >
                      <span>
                        <Button
                          onClick={() => handleOpenGrantDialog(firm)}
                          size="small"
                          variant="contained"
                          disabled={!firm.stripeCustomerId}
                          sx={{
                            borderRadius: "8px",
                            height: "30px",
                            textTransform: "none",
                            fontSize: "0.85rem",
                            px: 1.25,
                            minWidth: 0,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Grant Credits
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {filteredDesignFirms.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={10}
                  align="center"
                  sx={{ py: 3, color: "text.secondary" }}
                >
                  {showArchived
                    ? "No design firms found"
                    : "No non-archived design firms found"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Design Firm Modal */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="design-firm-modal"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            color: "text.primary",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Typography
            variant="h6"
            component="h2"
            gutterBottom
            color="text.primary"
          >
            {isEditMode ? "Edit Design Firm" : "Create Design Firm"}
          </Typography>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                name="name"
                label="Firm Name"
                fullWidth
                variant="outlined"
                value={newFirmData.name || ""}
                onChange={handleInputChange}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="domain"
                label="Domain"
                fullWidth
                variant="outlined"
                value={newFirmData.domain || ""}
                onChange={handleInputChange}
                required
                helperText="Will be normalized to base domain (e.g., example.com)"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="seats"
                label="Seats"
                type="number"
                fullWidth
                variant="outlined"
                inputProps={{ min: 1 }}
                value={newFirmData.seats || ""}
                onChange={handleInputChange}
                required
                helperText="Number of seats (minimum 1)"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="stripeCustomerId"
                label="Stripe Customer ID (Optional)"
                fullWidth
                variant="outlined"
                value={newFirmData.stripeCustomerId || ""}
                onChange={handleInputChange}
                helperText="Used for team credit pooling and grants (e.g., cus_...)"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="stripeSubscriptionId"
                label="Stripe Subscription ID (Optional)"
                fullWidth
                variant="outlined"
                value={newFirmData.stripeSubscriptionId || ""}
                onChange={handleInputChange}
                helperText="Must be unique and will be verified with Stripe"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={newFirmData.status || DesignFirmStatus.ACTIVE}
                  label="Status"
                  onChange={handleInputChange}
                >
                  <MenuItem value={DesignFirmStatus.ACTIVE}>Active</MenuItem>
                  <MenuItem value={DesignFirmStatus.ARCHIVED}>
                    Archived
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* <Grid item xs={12}>
              <TextField
                name="website"
                label="Website (Optional)"
                fullWidth
                variant="outlined"
                value={newFirmData.website || ''}
                onChange={handleInputChange}
              />
            </Grid> */}

            {errorMessage && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ mt: 1 }}>
                  {errorMessage}
                </Alert>
              </Grid>
            )}

            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mt: 2,
              }}
            >
              <Button
                onClick={handleCloseModal}
                color="inherit"
                sx={{ color: "text.secondary" }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateOrUpdateFirm}
                variant="contained"
                color="primary"
                disabled={
                  isSubmitting ||
                  !newFirmData.name ||
                  !newFirmData.domain ||
                  !newFirmData.seats
                }
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
              >
                {isSubmitting
                  ? "Processing..."
                  : isEditMode
                    ? "Update"
                    : "Create"}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>

      <Dialog
        open={grantDialogOpen}
        onClose={handleCloseGrantDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Grant Credits {grantFirm?.name ? `to ${grantFirm.name}` : ""}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
            This action grants credits directly to the design firm.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Number of Credits"
            type="number"
            fullWidth
            variant="outlined"
            value={grantAmount}
            onChange={(e) => setGrantAmount(e.target.value)}
            disabled={granting}
          />
          {grantError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {grantError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseGrantDialog} disabled={granting}>
            Cancel
          </Button>
          <Button
            onClick={handleGrantCredits}
            variant="contained"
            disabled={granting || !grantAmount}
          >
            {granting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Grant Credits"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <CustomSnackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default DesignFirmsManager;
